import React from 'react';
import { MapPin, Brain, BookOpen, Users, Target, Zap, ArrowRight, Sparkles } from 'lucide-react';
import SEO from '../SEO';
import { generateSEOMetadata, staticPages } from '../../utils/seo';

const Roadmap = () => {
  const features = [
    {
      id: 'microlearning',
      title: 'Integrated Microlearning',
      subtitle: 'Education woven into your feed',
      status: 'in-development',
      timeline: 'Q4 2025',
      icon: <BookOpen className="w-8 h-8" />,
      gradient: 'from-green-500 to-emerald-600',
      description:
        'Contextual learning modules that appear naturally in your feed, turning scroll time into growth time.',
      keyPoints: [
        "Bite-sized lessons that complement the content you're reading",
        'Adaptive difficulty based on your knowledge level',
        'Covers a wide range of topics, including critical thinking, media literacy, and subject expertise',
        'Gamified progress tracking with rewards'
      ],
      impact: 'Transforms passive consumption into active learning and intellectual growth'
    },
    {
      id: 'bias-space',
      title: 'Bias Space Technology',
      subtitle: 'Multi-dimensional ideological mapping',
      status: 'in-development',
      timeline: 'Q1 2026',
      icon: <Brain className="w-8 h-8" />,
      gradient: 'from-purple-500 to-indigo-600',
      description:
        'Novel system that maps content and users across a number of dimensions of perspective, preventing echo chambers and enabling genuine cross-ideological discourse.',
      keyPoints: [
        'Maps dimensions such as: Economic, Cultural, Authority Trust, Individual vs Collective, Evidence Standards, Tech/Nature, Scope, and Political alignment',
        'Prevents gaming by weighting cross-ideological engagement higher',
        'Enables "blind spot" recommendations to challenge your perspective',
        'Creates bridges between different worldviews through quality content'
      ],
      impact: 'Breaks the echo chamber cycle that radicalizes users and fragments society'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'in-development':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'planned':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'in-development':
        return 'In Development';
      case 'planned':
        return 'Planned';
      default:
        return 'Unknown';
    }
  };

  // Generate SEO metadata for roadmap page
  const seoMetadata = generateSEOMetadata.staticPage(
    staticPages.roadmap.name,
    staticPages.roadmap.url,
    staticPages.roadmap.description
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-12 px-4">
      <SEO {...seoMetadata} />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-6 py-3 shadow-lg mb-6">
            <MapPin className="w-5 h-5 text-green-600" />
            <span className="font-semibold text-gray-800">Product Roadmap</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Building the Future of
            <span className="bg-gradient-to-r from-green-600 to-purple-600 bg-clip-text text-transparent">
              {' '}
              Discourse
            </span>
          </h1>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Beyond better algorithms — we're building infrastructure for human understanding and
            learning. Here's what's coming to transform how we discuss, learn, and connect online.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative mb-16">
          {/* Desktop Timeline Line */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-green-400 to-purple-400 rounded-full"></div>

          <div className="space-y-8 md:space-y-16">
            {features.map((feature, index) => (
              <div
                key={feature.id}
                className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'md:flex-row-reverse'}`}
              >
                {/* Desktop Timeline dot */}
                <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-white border-4 border-green-500 rounded-full shadow-lg z-10"></div>

                {/* Content card */}
                <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                  <div className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden group">
                    {/* Header */}
                    <div
                      className={`bg-gradient-to-r ${feature.gradient} p-6 text-white relative overflow-hidden`}
                    >
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                          <div className="bg-white/20 rounded-xl p-3">{feature.icon}</div>
                          <div className="text-right">
                            <div
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(feature.status)}`}
                            >
                              {getStatusText(feature.status)}
                            </div>
                            <p className="text-white/80 text-sm mt-1">{feature.timeline}</p>
                          </div>
                        </div>
                        <h3 className="text-2xl font-bold mb-2">{feature.title}</h3>
                        <p className="text-white/90 font-medium">{feature.subtitle}</p>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <p className="text-gray-700 leading-relaxed mb-6">{feature.description}</p>

                      {/* Key Points */}
                      <div className="space-y-3 mb-6">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Target className="w-4 h-4 text-green-600" />
                          Key Capabilities
                        </h4>
                        <ul className="space-y-2">
                          {feature.keyPoints.map((point, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <div className="w-2 h-2 bg-gradient-to-r from-green-400 to-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-sm text-gray-700 leading-relaxed">{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Impact */}
                      <div className="bg-gradient-to-r from-green-50 to-purple-50 rounded-xl p-4 border border-green-200">
                        <div className="flex items-start gap-3">
                          <Zap className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <h5 className="font-semibold text-gray-900 mb-1">Impact</h5>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {feature.impact}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline label */}
                <div
                  className={`hidden md:block w-2/12 ${index % 2 === 0 ? 'text-right pr-4' : 'text-left pl-4'}`}
                >
                  <div className="text-sm font-semibold text-gray-500">{feature.timeline}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Vision Statement */}
        <div className="bg-gradient-to-br from-green-600 via-green-700 to-purple-600 rounded-3xl p-8 md:p-12 text-white shadow-2xl">
          <div className="text-center">
            <div className="bg-white/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10" />
            </div>

            <h2 className="text-3xl md:text-4xl font-bold mb-6">The Bigger Picture</h2>

            <p className="text-lg leading-relaxed max-w-3xl mx-auto mb-8 opacity-95">
              These features work together toward a simple goal: better conversations that actually
              inform and connect us.
            </p>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="text-center">
                <Users className="w-8 h-8 mx-auto mb-3 opacity-90" />
                <h4 className="font-semibold mb-2">Bridge Divides</h4>
                <p className="text-sm opacity-80">
                  Connect people across ideological differences through quality content
                </p>
              </div>
              <div className="text-center">
                <Brain className="w-8 h-8 mx-auto mb-3 opacity-90" />
                <h4 className="font-semibold mb-2">Expand Minds</h4>
                <p className="text-sm opacity-80">
                  Turn scroll time into learning time with contextual education
                </p>
              </div>
              <div className="text-center">
                <Target className="w-8 h-8 mx-auto mb-3 opacity-90" />
                <h4 className="font-semibold mb-2">Reward Quality</h4>
                <p className="text-sm opacity-80">
                  Make thoughtful discourse more profitable than outrage
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-6">Want to help shape the future of online discourse?</p>
          <button className="bg-gradient-to-r from-green-600 to-green-700 text-white font-bold py-4 px-8 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center mx-auto group">
            Join the Beta
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Roadmap;
